/**
 * ARTICLE D'EXEMPLE — généré le 2026-09-06 pour valider la maquette du blog.
 * À relire, compléter ou remplacer par du contenu réel avant/après mise en ligne.
 * Sources : 6 URL vérifiées automatiquement (6 OK).
 * Format : voir src/blog/README.md
 */
export default {
  "slug": "femto-st-besancon-capitale-microtechniques",
  "title": "FEMTO-ST, l’institut qui fait de Besançon une capitale des microtechniques",
  "excerpt": "Temps-fréquence, MEMS, photonique, microrobotique : portrait de FEMTO-ST, l’institut CNRS de plus de 700 personnes qui structure la deeptech bisontine et ouvre des pistes pour l’IA embarquée.",
  "seoTitle": "FEMTO-ST, institut CNRS des microtechniques",
  "seoDescription": "Temps-fréquence, MEMS, photonique, microrobotique : portrait de FEMTO-ST, l’unité de recherche CNRS de plus de 700 personnes qui irrigue la deeptech locale.",
  "category": "recherche",
  "tags": [
    "FEMTO-ST",
    "Microtechniques",
    "Deeptech",
    "Recherche",
    "Capteurs"
  ],
  "author": "jimmy",
  "publishedAt": "2026-09-04",
  "updatedAt": "2026-09-04",
  "featured": true,
  "cover": {
    "image": "/img/blog/unsplash-1581093588401-fbb62a02f120.jpg",
    "credit": "Photo : Unsplash",
    "alt": "Chercheur en combinaison blanche manipulant une plaquette de silicium dans une salle blanche de microfabrication, sous un éclairage jaune."
  },
  "content": [
    {
      "type": "lead",
      "text": "À Besançon, la recherche en microtechniques porte un nom : FEMTO-ST. Placé sous la tutelle du CNRS, de l’Université Marie et Louis Pasteur, de SUPMICROTECH-ENSMM et de l’UTBM, cet institut concentre des compétences rares en temps-fréquence, microsystèmes, photonique et microrobotique. Pour la tech locale, c’est un vivier de talents, une source de brevets et un point d’ancrage pour les start-up deeptech."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Nom complet",
          "value": "Franche-Comté Électronique Mécanique Thermique et Optique – Sciences et Technologies"
        },
        {
          "label": "Statut",
          "value": "Unité mixte de recherche CNRS (UMR 6174) avec l’Université Marie et Louis Pasteur, SUPMICROTECH-ENSMM et l’UTBM"
        },
        {
          "label": "Création",
          "value": "2004, par regroupement de laboratoires francs-comtois"
        },
        {
          "label": "Effectif",
          "value": "Plus de 700 personnes, sept départements scientifiques"
        },
        {
          "label": "Implantations",
          "value": "Besançon (technopole TEMIS), Belfort et Montbéliard"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Un héritage horloger devenu laboratoire de pointe"
    },
    {
      "type": "p",
      "text": "Besançon a longtemps été la capitale française de l’horlogerie. Quand cette industrie a décliné, la ville a conservé quelque chose de plus durable qu’une filière : une culture de la précision, de la mécanique fine et de la mesure du temps. C’est sur ce terreau que s’est construit [FEMTO-ST](https://www.femto-st.fr/), acronyme de « Franche-Comté Électronique Mécanique Thermique et Optique – Sciences et Technologies ».[^1]"
    },
    {
      "type": "p",
      "text": "L’institut est né en 2004 du regroupement de plusieurs laboratoires de Besançon, Belfort et Montbéliard jusque-là distincts. Il fonctionne comme une unité mixte de recherche du CNRS, avec pour cotutelles l’Université de Franche-Comté, devenue Université Marie et Louis Pasteur début 2025, l’école d’ingénieurs SUPMICROTECH-ENSMM et l’UTBM.[^2][^3][^4] Avec plus de 700 personnes, chercheurs, ingénieurs, techniciens et doctorants, répartis en sept départements scientifiques, il compte parmi les plus grandes unités mixtes de recherche associées au CNRS en France.[^1]"
    },
    {
      "type": "h2",
      "text": "Quatre spécialités qui font la réputation de Besançon"
    },
    {
      "type": "p",
      "text": "Ces départements couvrent un spectre large, de la mécanique appliquée à l’informatique en passant par l’énergie. Quatre thématiques donnent toutefois à l’institut sa visibilité internationale et façonnent directement l’écosystème local."
    },
    {
      "type": "ul",
      "items": [
        "**Temps-fréquence** : oscillateurs ultra-stables, résonateurs à quartz, horloges atomiques et métrologie du temps, en héritage direct de la tradition horlogère.",
        "**Micro et nanosystèmes (MEMS)** : conception et fabrication de microsystèmes dans la centrale de technologie MIMENTO, une salle blanche membre du réseau national RENATECH du CNRS. L’institut opère au total une dizaine de plateformes technologiques.[^1]",
        "**Optique et photonique** : optique non linéaire, optique intégrée, lasers et instrumentation.",
        "**Microrobotique et micromécatronique** : manipulation et assemblage d’objets de taille micrométrique, commande de systèmes à ces échelles."
      ]
    },
    {
      "type": "p",
      "text": "À ces piliers s’ajoutent un département Énergie, tourné vers la pile à combustible et l’hydrogène, et le département DISC (informatique des systèmes complexes), qui travaille notamment sur les systèmes distribués, la vérification de logiciels et l’apprentissage automatique.[^1]"
    },
    {
      "type": "h2",
      "text": "De la paillasse à la start-up"
    },
    {
      "type": "p",
      "text": "L’intérêt d’un tel institut pour la tech bisontine ne se limite pas aux publications. FEMTO-ST et les laboratoires qui l’ont précédé ont essaimé une vingtaine de sociétés depuis la fin des années 1980, dont plusieurs deeptech installées à Besançon : [Percipio Robotics](/blog/vivatech-2026-percipio-robotics-startups-bfc) (microrobotique et micro-assemblage), SilMach (microactionneurs MEMS), Photline Technologies (modulateurs optiques, aujourd’hui intégrée au groupe Exail), Aurea Technology (instrumentation photonique et quantique), Amarob (microchirurgie laser) ou encore Frec’n’sys (capteurs à ondes acoustiques, reprise par Soitec).[^5]"
    },
    {
      "type": "p",
      "text": "L’institut s’appuie aussi sur une structure de transfert, FEMTO Engineering, qui réalise des développements pour des industriels à partir des résultats de la recherche.[^1] Et tous les deux ans, [le salon Micronora](/blog/micronora-2026-salon-microtechniques-acteurs-ia) réunit à Besançon industriels et laboratoires des microtechniques ; l’édition 2026 est programmée fin septembre.[^6]"
    },
    {
      "type": "h2",
      "text": "Ce que cela ouvre : capteurs, IA embarquée, deeptech"
    },
    {
      "type": "p",
      "text": "Pour des développeurs IA, le lien avec un institut de microtechniques n’est pas évident au premier abord. Il le devient quand on regarde ce que produisent ces laboratoires : des capteurs, des références de temps, des actionneurs, des micro-instruments. Autrement dit, la couche matérielle sur laquelle une IA embarquée doit tourner. Plusieurs sujets se prêtent à des collaborations entre logiciel et matériel :"
    },
    {
      "type": "ol",
      "items": [
        "**Capteurs intelligents** : intégrer un modèle léger au plus près d’un capteur MEMS ou optique, pour traiter le signal localement sans remonter les données brutes vers un serveur.",
        "**Synchronisation et horodatage** : les réseaux de capteurs distribués et de nombreuses infrastructures critiques reposent sur des références de temps précises, un domaine où l’expertise bisontine est reconnue.",
        "**Microrobotique assistée par apprentissage** : à l’échelle micrométrique, les modèles physiques atteignent leurs limites ; la vision par ordinateur et l’apprentissage par renforcement y trouvent un terrain naturel.",
        "**Maintenance prédictive** : le pronostic de l’état de santé des systèmes énergétiques, comme les piles à combustible, repose largement sur l’analyse de données."
      ]
    },
    {
      "type": "p",
      "text": "Le vivier de compétences existe : [ingénieurs formés à SUPMICROTECH-ENSMM](/blog/se-former-ia-besancon-guide-voies-acces), diplômés de master de l’université, doctorants passés par les plateformes de FEMTO-ST avant de rejoindre une entreprise locale ou d’en fonder une.[^3][^4]"
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour l’IA",
      "text": "La plupart des discussions sur l’IA portent sur les modèles et les données. Besançon apporte l’autre moitié de l’équation : des capteurs, des références temporelles et des microsystèmes conçus sur place. Une IA embarquée commence par un bon signal en entrée, et c’est précisément ce que les équipes de FEMTO-ST savent produire."
    },
    {
      "type": "h2",
      "text": "Un socle plutôt qu’une vitrine"
    },
    {
      "type": "p",
      "text": "FEMTO-ST ne fera pas de Besançon une capitale des grands modèles de langage, et ce n’est pas son rôle. Sa contribution est plus structurelle : maintenir sur place une masse critique de compétences en microfabrication, en mesure du temps et en photonique, et la convertir régulièrement en entreprises. Pour l’écosystème IA local, l’enjeu des prochaines années est de mieux relier ces deux mondes, celui du logiciel et celui du micron, plutôt que de les laisser évoluer en parallèle.[^1][^2]"
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Institut FEMTO-ST – site officiel",
      "publisher": "FEMTO-ST",
      "url": "https://www.femto-st.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 2,
      "title": "CNRS – Centre national de la recherche scientifique",
      "publisher": "CNRS",
      "url": "https://www.cnrs.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 3,
      "title": "Université Marie & Louis Pasteur",
      "publisher": "Université Marie et Louis Pasteur",
      "url": "https://www.umlp.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 4,
      "title": "SUPMICROTECH-ENSMM – École nationale supérieure de mécanique et des microtechniques",
      "publisher": "SUPMICROTECH-ENSMM",
      "url": "https://www.supmicrotech.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 5,
      "title": "TEMIS Technopole Besançon – écosystème d’innovation deeptech",
      "publisher": "TEMIS",
      "url": "https://www.temis.org/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 6,
      "title": "Micronora – salon international des microtechniques et de la précision",
      "publisher": "Micronora",
      "url": "https://www.micronora.com/",
      "type": "official",
      "date": "2026-09-06"
    }
  ]
};
